/**
 * NotificationPrompt.js - Component to request notification permissions
 * Shows a non-intrusive banner asking users to enable notifications
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import notificationService from '../../services/notificationService';

const NotificationPrompt = ({ onDismiss }) => {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState('pending'); // pending, granted, denied

  useEffect(() => {
    // Check if notifications are supported and permission is not yet granted
    if (notificationService.isAvailable()) {
      const permission = notificationService.getPermissionStatus();
      if (permission === 'default') {
        // Show prompt after a short delay
        const timer = setTimeout(() => setShow(true), 3000);
        return () => clearTimeout(timer);
      } else {
        setStatus(permission);
      }
    }
  }, []);

  const handleEnable = async () => {
    setStatus('requesting');
    const granted = await notificationService.requestPermission();
    
    if (granted) {
      setStatus('granted');
      // Hide after showing success
      setTimeout(() => {
        setShow(false);
        onDismiss?.();
      }, 2000);
    } else {
      setStatus('denied');
    }
  };

  const handleDismiss = () => {
    setShow(false);
    onDismiss?.();
    // Store in localStorage to not show again
    localStorage.setItem('notification_prompt_dismissed', 'true');
  };

  // Don't show if already dismissed
  useEffect(() => {
    if (localStorage.getItem('notification_prompt_dismissed') === 'true') {
      setShow(false);
    }
  }, []);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-40"
      >
        <div className="bg-[var(--background-paper)] rounded-xl shadow-2xl border border-[var(--border-subtle)] overflow-hidden">
          <div className="p-4">
            <div className="flex items-start gap-4">
              <div className={`p-2 rounded-lg ${
                status === 'granted' ? 'bg-green-500/20' : 'bg-[#D4AF37]/20'
              }`}>
                {status === 'granted' ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : status === 'denied' ? (
                  <BellOff className="w-6 h-6 text-gray-500" />
                ) : (
                  <Bell className="w-6 h-6 text-[#D4AF37]" />
                )}
              </div>
              
              <div className="flex-1">
                {status === 'granted' ? (
                  <>
                    <h4 className="font-semibold text-green-500">Notifications Enabled</h4>
                    <p className="text-sm text-[var(--text-muted)] mt-1">
                      You will receive notifications for calls, messages, and updates.
                    </p>
                  </>
                ) : status === 'denied' ? (
                  <>
                    <h4 className="font-semibold">Notifications Blocked</h4>
                    <p className="text-sm text-[var(--text-muted)] mt-1">
                      Enable notifications in your browser settings to receive call alerts.
                    </p>
                  </>
                ) : (
                  <>
                    <h4 className="font-semibold">Enable Notifications</h4>
                    <p className="text-sm text-[var(--text-muted)] mt-1">
                      Get notified when you receive calls, messages, or support updates.
                    </p>
                  </>
                )}
              </div>
              
              <button
                onClick={handleDismiss}
                className="p-1 rounded-lg hover:bg-[var(--background-elevated)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {status !== 'granted' && status !== 'denied' && (
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDismiss}
                  className="flex-1"
                >
                  Maybe Later
                </Button>
                <Button
                  size="sm"
                  onClick={handleEnable}
                  disabled={status === 'requesting'}
                  className="flex-1 btn-primary"
                >
                  {status === 'requesting' ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationPrompt;
